import React, { CSSProperties, useMemo, useState } from 'react'
import { List, Row, Tag } from 'antd'
import moment from 'moment'
import { useDispatch, useSelector } from 'react-redux'

import { ButtonTitled, Empty, Text } from 'src/components/Ant'
import { getAirHistory } from 'src/core/store/ducks/air/actions'
import {
  Request,
  RequestQuoteAir,
  RequestQuoteAirFare,
  RequestQuoteAirFlight,
  RequestTicketStatus,
  ResumeAirRequestQuoteAirs
} from 'src/types'
import CurrencyMask from 'src/components/Formik/InputCurrency/mask'
import Images from 'src/config/images'
import UnifiedTag from 'src/views/Requests/components/Table/components/UnifiedTag'
import { ApplicationState } from 'src/store'

import ModalHistory from '../modal-history'
import { WrapCard } from '../../../../../styles'

type Props = {
  bodyStyle?: CSSProperties
  handleDelete?(item: SelectedFlight): void
  request: Request
}

type SelectedFlight = Pick<
  RequestQuoteAirFare,
  'family' | 'request_quote_fare_hash'
> &
  Pick<
    RequestQuoteAir,
    | 'air_destiny'
    | 'arrival_date'
    | 'air_origin'
    | 'company'
    | 'departure_date'
    | 'stops'
  > &
  Pick<RequestQuoteAirFlight, 'flight_code'> & {
    arrival_date_formatted: string
    departure_date_formatted: string
    eticket_code?: string
    status?: RequestTicketStatus
    total_value?: number
  }

function SummaryCard({ bodyStyle = {}, handleDelete, request }: Props) {
  const [visibleModalHistory, setVisibleModalHistory] = useState<boolean>(false)
  const dispatch = useDispatch()

  const { air } = useSelector((state: ApplicationState) => ({
    air: state.air
  }))

  const selectedFlights = useMemo<SelectedFlight[]>(() => {
    const items: SelectedFlight[] = []

    if (!request.request_id) {
      const selectedAirs: ResumeAirRequestQuoteAirs[] = []

      const searchKeys = Object.keys(air.searches)

      searchKeys.forEach(key => {
        air.searches[key].list.forEach(resumeAirItem => {
          resumeAirItem.request_quote_airs.forEach(quoteAirItem => {
            quoteAirItem.request_quote_air_fares.forEach(fareItem => {
              const hasAdded = !!request.full_request_air.filter(
                fullRequestAirItem =>
                  fullRequestAirItem.request_quote_air_fare_hash.includes(
                    fareItem.request_quote_fare_hash
                  )
              ).length

              if (hasAdded) {
                selectedAirs.push({
                  ...quoteAirItem,
                  request_quote_air_fares: [{ ...fareItem }]
                })
              }
            })
          })
        })
      })

      if (!selectedAirs.length) {
        return []
      }

      return selectedAirs.map<SelectedFlight>(airItem => {
        const arrival_date = moment.unix(airItem.arrival_date).utc()
        const departure_date = moment.unix(airItem.departure_date).utc()

        return {
          request_quote_fare_hash:
            airItem.request_quote_air_fares[0].request_quote_fare_hash,
          air_destiny: airItem.air_destiny,
          air_origin: airItem.air_origin,
          arrival_date: arrival_date.unix(),
          arrival_date_formatted: arrival_date.format('DD/MM/YYYY HH:mm'),
          company: airItem.company,
          departure_date: departure_date.unix(),
          departure_date_formatted: departure_date.format('DD/MM/YYYY HH:mm'),
          family: airItem.request_quote_air_fares[0].family,
          flight_code: airItem.flight_code,
          stops: airItem.stops,
          total_value: airItem.request_quote_air_fares[0].total_value
        }
      })
    }
    request.request_tickets
      .filter(
        requestTicketItem =>
          requestTicketItem.class_type === 'SERVICE' &&
          requestTicketItem.request_ticket_type === 'AIR'
      )
      .forEach(requestTicketItem => {
        items.push({
          request_quote_fare_hash: requestTicketItem.request_ticket_id,
          air_destiny:
            requestTicketItem.request_ticket_air_selected.request_quote_air_trip
              .request_quote_air_one_way.air_destiny,
          air_origin:
            requestTicketItem.request_ticket_air_selected.request_quote_air_trip
              .request_quote_air_one_way.air_origin,
          arrival_date:
            requestTicketItem.request_ticket_air_selected.request_quote_air_trip
              .request_quote_air_one_way.arrival_date,
          arrival_date_formatted: moment
            .unix(
              requestTicketItem.request_ticket_air_selected
                .request_quote_air_trip.request_quote_air_one_way.arrival_date
            )
            .utc()
            .format('DD/MM/YYYY HH:mm'),
          company:
            requestTicketItem.request_ticket_air_selected.request_quote_air_trip
              .request_quote_air_one_way.company,
          departure_date:
            requestTicketItem.request_ticket_air_selected.request_quote_air_trip
              .request_quote_air_one_way.departure_date,
          departure_date_formatted: moment
            .unix(
              requestTicketItem.request_ticket_air_selected
                .request_quote_air_trip.request_quote_air_one_way.departure_date
            )
            .utc()
            .format('DD/MM/YYYY HH:mm'),

          family:
            requestTicketItem.request_ticket_air_selected.request_quote_air_trip
              .request_quote_air_one_way.request_quote_air_fares[0].family,
          flight_code:
            requestTicketItem.request_ticket_air_selected.request_quote_air_trip
              .request_quote_air_one_way.request_quote_air_flights[0]
              .flight_code,
          stops:
            requestTicketItem.request_ticket_air_selected.request_quote_air_trip
              .request_quote_air_one_way.stops,
          total_value:
            requestTicketItem.request_ticket_air_selected.request_quote_air_trip
              .total_air_value,
          eticket_code:
            requestTicketItem.request_ticket_air_selected.eticket_code,
          status: requestTicketItem.status
        })

        if (
          requestTicketItem.request_ticket_air_selected.request_quote_air_trip
            .request_quote_air_round_trip.request_quote_air_id
        ) {
          items.push({
            request_quote_fare_hash: requestTicketItem.request_ticket_id,
            air_destiny:
              requestTicketItem.request_ticket_air_selected
                .request_quote_air_trip.request_quote_air_round_trip
                .air_destiny,
            air_origin:
              requestTicketItem.request_ticket_air_selected
                .request_quote_air_trip.request_quote_air_round_trip.air_origin,
            arrival_date:
              requestTicketItem.request_ticket_air_selected
                .request_quote_air_trip.request_quote_air_round_trip
                .arrival_date,
            arrival_date_formatted: moment
              .unix(
                requestTicketItem.request_ticket_air_selected
                  .request_quote_air_trip.request_quote_air_round_trip
                  .arrival_date
              )
              .utc()
              .format('DD/MM/YYYY HH:mm'),
            company:
              requestTicketItem.request_ticket_air_selected
                .request_quote_air_trip.request_quote_air_round_trip.company,
            departure_date:
              requestTicketItem.request_ticket_air_selected
                .request_quote_air_trip.request_quote_air_round_trip
                .departure_date,
            departure_date_formatted: moment
              .unix(
                requestTicketItem.request_ticket_air_selected
                  .request_quote_air_trip.request_quote_air_round_trip
                  .departure_date
              )
              .utc()
              .format('DD/MM/YYYY HH:mm'),
            family:
              requestTicketItem.request_ticket_air_selected
                .request_quote_air_trip.request_quote_air_round_trip
                .request_quote_air_fares[0].family,
            flight_code:
              requestTicketItem.request_ticket_air_selected
                .request_quote_air_trip.request_quote_air_round_trip
                .request_quote_air_flights[0].flight_code,
            stops:
              requestTicketItem.request_ticket_air_selected
                .request_quote_air_trip.request_quote_air_round_trip.stops,
            eticket_code:
              requestTicketItem.request_ticket_air_selected.eticket_code,
            status: requestTicketItem.status
          })
        }
      })

    return items
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request.full_request_air, request.request_tickets])

  const hasHistory = useMemo<boolean>(
    () =>
      typeof request?.request_id === 'number' && selectedFlights?.length >= 1,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [request?.request_id, selectedFlights]
  )
  return (
    <>
      <WrapCard
        data-test-id="airs-summary"
        bodyStyle={bodyStyle}
        extra={[
          hasHistory && (
            <ButtonTitled
              key="hotel-history"
              icon="history"
              onClick={() => {
                if (request?.request_id) {
                  dispatch(getAirHistory({ request_id: request.request_id }))
                }
                setVisibleModalHistory(true)
              }}
              size="default"
              tooltip="common.actions.historical"
            />
          )
        ]}
        title="web.services.airs.list-title"
      >
        <List
          data-test-id="airs-summary-list"
          itemLayout="horizontal"
          dataSource={selectedFlights}
          locale={{ emptyText: <Empty onlyText /> }}
          renderItem={(item, index) => (
            <List.Item
              actions={[
                ...(handleDelete
                  ? [
                      <ButtonTitled
                        data-test-id={`airs-summary-item-delete-${index}`}
                        icon="delete"
                        onClick={() => handleDelete(item)}
                        shape="circle"
                        tooltip="common.actions.remove"
                        type="danger"
                      />
                    ]
                  : [])
              ]}
            >
              <List.Item.Meta
                avatar={
                  <img
                    alt={item.company}
                    src={Images.airlines[item.company] || Images.noImage}
                    style={{ maxHeight: 45, maxWidth: 45 }}
                  />
                }
                title={
                  <>
                    <Text colon strong title="web.services.airs.form.flight" />
                    <Text>{item.flight_code}</Text>
                  </>
                }
                description={
                  <>
                    {!!item.status && <UnifiedTag status={item.status} />}
                    {!!item.eticket_code && (
                      <Row>
                        <Text colon title="web.services.airs.form.emission" />
                        <Text>{item.eticket_code}</Text>
                      </Row>
                    )}
                    <Row>
                      <Text
                        colon
                        strong
                        title="web.services.airs.form.origin.title"
                      />
                      <Text>
                        {`${item.air_origin} (${item.departure_date_formatted})`}
                      </Text>
                    </Row>
                    <Row>
                      <Text
                        colon
                        strong
                        title="web.services.airs.form.destination.title"
                      />
                      <Text>{`${item.air_destiny} (${item.arrival_date_formatted})`}</Text>
                    </Row>
                    {!!item.total_value && (
                      <Row>
                        <Tag
                          color="green"
                          style={{
                            fontSize: 20,
                            padding: 4,
                            textAlign: 'center'
                          }}
                        >
                          {`BRL ${
                            CurrencyMask({
                              formatStr: true,
                              value: item.total_value
                            }).maskedValue
                          }`}
                        </Tag>
                      </Row>
                    )}
                  </>
                }
              />
            </List.Item>
          )}
        />
      </WrapCard>
      <ModalHistory
        toggle={() => {
          setVisibleModalHistory(false)
        }}
        visible={visibleModalHistory}
      />
    </>
  )
}

export default SummaryCard
